import * as React from 'react';
import { PaymentCycle } from 'enums';
import { CycleDetails } from 'types';

interface CyclePopoverState {
  cycleId: any;
  isOpened: boolean;
}

const PopoverContext = React.createContext<{
  popovers: CyclePopoverState[];
  setPopovers: React.Dispatch<React.SetStateAction<CyclePopoverState[]>>;
}>(null as any);

export const usePopoverState = (cycleId: PaymentCycle) => {
  const { popovers } = React.useContext(PopoverContext);
  return popovers.find((p) => p.cycleId === cycleId)?.isOpened;
};

export const CyclePopoverProvider: React.FC<{
  cycles: { cycleDetails: CycleDetails }[];
}> = ({ children, cycles }) => {
  const [popovers, setPopovers] = React.useState<CyclePopoverState[]>(
    cycles.map(({ cycleDetails: { cycleId } }) => ({
      isOpened: false,
      cycleId,
    })),
  );
  return (
    <PopoverContext.Provider value={{ popovers, setPopovers }}>
      {children}
    </PopoverContext.Provider>
  );
};

export const CyclePopoverWrapper: React.FC<{
  cycleDetails: CycleDetails;
}> = ({ children, cycleDetails }) => {
  const { setPopovers } = React.useContext(PopoverContext);
  const { cycleId, isCycleDisabledInProduct } = cycleDetails;

  const handleMouseEnter = React.useCallback(() => {
    setPopovers((currentPopoversState) =>
      currentPopoversState.map((popover) =>
        popover.cycleId === cycleId
          ? { ...popover, isOpened: isCycleDisabledInProduct }
          : popover,
      ),
    );
  }, []);

  const handleMouseLeave = React.useCallback(() => {
    setPopovers((currentPopoverState) =>
      currentPopoverState.map((popover) => ({ ...popover, isOpened: false })),
    );
  }, []);

  const isEdgeNonChromium = !!navigator.userAgent.match(/Edge\/1.*/); // avoid wrapping with `display: contents`

  return isEdgeNonChromium ? (
    <>{children}</>
  ) : (
    <div
      data-hook='cycle-popover'
      style={{ display: 'contents' }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
    </div>
  );
};
